import { supabaseCustomRole } from "@/lib/supabase/customRoleClient";
import { DealerFilterState } from "../types";
import { formatCityFromSlug, generateCityVariants } from "@/utils/cityUtils";
import { sortDealersByPriority } from "@/hooks/recommended-dealers/utils/sortingUtils";
import { Dealer } from "@/types/dealer";

export const buildStandardQuery = (
  filters: DealerFilterState,
  city?: string
) => {
  let query = supabaseCustomRole
    .from('vw_dealers_with_ratings')
    .select('*', { count: 'exact' });

  // Apply filters server-side
  if (filters.filterActivated) {
    query = query.eq('activated', true);
  }

  if (filters.search && filters.search.trim()) {
    const searchTerm = filters.search.trim();
    query = query.or(`name.ilike.%${searchTerm}%,address.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
  }

  if (city) {
    // Convert URL slug to city variants for better matching
    const cityVariants = generateCityVariants(city);
    const cityConditions = cityVariants.map(variant => `city.ilike.%${variant}%`).join(',');
    query = query.or(cityConditions);
  }

  // Filter by rating range
  if (filters.range && filters.range[0] > 0) {
    query = query.gte('average_rating', filters.range[0]);
  }
  if (filters.range && filters.range[1] < 5) {
    query = query.lte('average_rating', filters.range[1]);
  }

  return query;
};

export const applySorting = (
  query: any,
  sortOrder: DealerFilterState['sortOrder']
) => {
  switch (sortOrder) {
    case 'rating':
      return query.order('average_rating', { ascending: false });
    case 'name':
      return query.order('name', { ascending: true });
    case 'asc':
      return query.order('average_rating', { ascending: true });
    case 'desc':
      return query.order('average_rating', { ascending: false });
    default:
      // Default: order by activated first, then by rating
      return query.order('activated', { ascending: false })
                  .order('average_rating', { ascending: false });
  }
};

/**
 * Enhanced sorting that applies intelligent distance-based sorting when coordinates are available
 * Falls back to database sorting when coordinates are not available
 */
export const applyEnhancedSorting = (
  dealers: Dealer[],
  sortOrder: DealerFilterState['sortOrder'],
  coordinates?: { latitude: number; longitude: number } | null
): Dealer[] => {
  // If we have coordinates and no specific sort order is set, use intelligent sorting
  if (coordinates && !sortOrder) {
    console.log('🎯 applyEnhancedSorting: Using intelligent distance-based sorting (no sort order specified)');
    return sortDealersByPriority(dealers, coordinates);
  }

  // For specific sort orders or when no coordinates, apply client-side sorting
  console.log('📍 applyEnhancedSorting: Using standard sorting for', sortOrder);
  
  switch (sortOrder) {
    case 'rating':
      return [...dealers].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    case 'name':
      return [...dealers].sort((a, b) => a.name.localeCompare(b.name));
    case 'asc':
      return [...dealers].sort((a, b) => (a.rating || 0) - (b.rating || 0));
    case 'desc':
      return [...dealers].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    case 'distance':
      if (coordinates) {
        // Distance sorting is handled by sortDealersByPriority
        console.log('🎯 applyEnhancedSorting: Using intelligent distance-based sorting (distance sort order)');
        return sortDealersByPriority(dealers, coordinates);
      }
      // Fallback to rating if no coordinates
      return [...dealers].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    default:
      // If we have coordinates, use intelligent sorting as default
      if (coordinates) {
        console.log('🎯 applyEnhancedSorting: Using intelligent distance-based sorting (default with coordinates)');
        return sortDealersByPriority(dealers, coordinates);
      }
      // Default without coordinates: activated first, then by rating
      return [...dealers].sort((a, b) => {
        // First sort by activated status
        if (a.activated !== b.activated) {
          return b.activated ? 1 : -1;
        }
        // Then by rating
        return (b.rating || 0) - (a.rating || 0);
      });
  }
};

export const executeDistanceQuery = async (
  coordinates: { latitude: number; longitude: number },
  filters: DealerFilterState,
  offset: number,
  limit: number,
  city?: string
) => {
  const { latitude, longitude } = coordinates;
  
  try {
    let query;
    
    if (filters.distanceRange > 0) {
      // Try RPC for distance filtering
      query = supabaseCustomRole
        .rpc('dealers_within_distance', {
          lat: latitude,
          lng: longitude,
          distance_km: filters.distanceRange
        });
    } else {
      // Try RPC for distance sorting
      query = supabaseCustomRole
        .rpc('dealers_by_distance', {
          lat: latitude,
          lng: longitude
        });
    }
    
    // Apply city filter if provided - use improved city matching
    if (city) {
      const cityVariants = generateCityVariants(city);
      const cityConditions = cityVariants.map(variant => `city.ilike.%${variant}%`).join(',');
      query = query.or(cityConditions);
    }
    
    // Apply other filters
    if (filters.filterActivated) {
      query = query.eq('activated', true);
    }
    
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.trim();
      query = query.or(`name.ilike.%${searchTerm}%,address.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
    }
    
    // Filter by rating range
    if (filters.range && filters.range[0] > 0) {
      query = query.gte('average_rating', filters.range[0]);
    }
    if (filters.range && filters.range[1] < 5) {
      query = query.lte('average_rating', filters.range[1]);
    }
    
    // Get the result with pagination
    const result = await query.range(offset, offset + limit - 1);
    
    // For RPC functions, we need to manually calculate the count since they don't support { count: 'exact' }
    // We'll return the result and let the calling function handle the count
    return {
      ...result,
      // Note: RPC functions don't return count, so we'll need to handle this differently
      count: null
    };
    
  } catch (error: any) {
    console.warn('RPC functions not available, falling back to standard query:', error.message);
    
    // Fallback to standard query without distance calculation
    let fallbackQuery = buildStandardQuery(filters, city);
    fallbackQuery = applySorting(fallbackQuery, 'rating'); // Use rating sort as fallback
    fallbackQuery = fallbackQuery.range(offset, offset + limit - 1);
    
    return await fallbackQuery;
  }
};

/**
 * Get total count for infinite scroll - FIXED for proper pagination
 * Now supports distance-based filtering with coordinates
 */
export const getTotalCount = async (
  filters: DealerFilterState,
  coordinates?: { latitude: number; longitude: number } | null,
  city?: string
) => {
  try {
    // For distance-based queries, we need to use a different approach
    // since RPC functions don't support count directly
    if (coordinates && (filters.sortOrder === 'distance' || filters.distanceRange > 0)) {
      console.log('🔍 getTotalCount: Using distance-based count calculation');
      
      try {
        let countQuery;
        
        if (filters.distanceRange > 0) {
          // For distance filtering, get all results and count them
          countQuery = supabaseCustomRole
            .rpc('dealers_within_distance', {
              lat: coordinates.latitude,
              lng: coordinates.longitude,
              distance_km: filters.distanceRange
            });
        } else {
          // For distance sorting, get all results and count them
          countQuery = supabaseCustomRole
            .rpc('dealers_by_distance', {
              lat: coordinates.latitude,
              lng: coordinates.longitude
            });
        }
        
        // Apply same filters as main query
        if (city) {
          const cityVariants = generateCityVariants(city);
          const cityConditions = cityVariants.map(variant => `city.ilike.%${variant}%`).join(',');
          countQuery = countQuery.or(cityConditions);
        }
        
        if (filters.filterActivated) {
          countQuery = countQuery.eq('activated', true);
        }
        
        if (filters.search && filters.search.trim()) {
          const searchTerm = filters.search.trim();
          countQuery = countQuery.or(`name.ilike.%${searchTerm}%,address.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
        }
        
        // Filter by rating range
        if (filters.range && filters.range[0] > 0) {
          countQuery = countQuery.gte('average_rating', filters.range[0]);
        }
        if (filters.range && filters.range[1] < 5) {
          countQuery = countQuery.lte('average_rating', filters.range[1]);
        }
        
        const { data, error } = await countQuery;
        
        if (error) {
          console.warn('Error getting distance-based count, falling back to standard count:', error);
          // Fallback to standard count
        } else if (data) {
          console.log(`📊 Distance-based count: ${data.length}`);
          return data.length;
        }
      } catch (error) {
        console.warn('Distance-based count failed, falling back to standard count:', error);
        // Continue to standard count below
      }
    }

    // Standard count query for non-distance cases or fallback
    console.log('🔍 getTotalCount: Using standard count calculation');
    
    let countQuery = supabaseCustomRole
      .from('vw_dealers_with_ratings')
      .select('id', { count: 'exact' });

    // Apply same filters as main query
    if (filters.filterActivated) {
      countQuery = countQuery.eq('activated', true);
    }

    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.trim();
      countQuery = countQuery.or(`name.ilike.%${searchTerm}%,address.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
    }

    if (city) {
      const cityVariants = generateCityVariants(city);
      const cityConditions = cityVariants.map(variant => `city.ilike.%${variant}%`).join(',');
      countQuery = countQuery.or(cityConditions);
    }

    // Filter by rating range
    if (filters.range && filters.range[0] > 0) {
      countQuery = countQuery.gte('average_rating', filters.range[0]);
    }
    if (filters.range && filters.range[1] < 5) {
      countQuery = countQuery.lte('average_rating', filters.range[1]);
    }
    
    const { count, error } = await countQuery;
    
    if (error) {
      console.error("Error getting total count:", error);
      return 0;
    }
    
    console.log(`📊 Standard count: ${count || 0}`);
    return count || 0;
  } catch (error) {
    console.error("Error in getTotalCount:", error);
    return 0;
  }
};

/**
 * Enhanced query execution that combines database querying with intelligent client-side sorting
 * FIXED: Proper infinite scroll support with correct count handling for distance queries
 */
export const executeEnhancedQuery = async (
  filters: DealerFilterState,
  coordinates: { latitude: number; longitude: number } | null,
  offset: number,
  limit: number,
  city?: string,
  signal?: AbortSignal
) => {
  try {
    console.log(`🔍 executeEnhancedQuery: Fetching dealers with enhanced sorting`);
    console.log(`📊 Filters:`, { sortOrder: filters.sortOrder, hasCoordinates: !!coordinates, offset, limit });

    // For distance-based queries with coordinates, use RPC functions (server-side pagination)
    if (filters.sortOrder === 'distance' && coordinates) {
      console.log('🎯 Using distance-based RPC query with server-side pagination');
      
      const result = await executeDistanceQuery(coordinates, filters, offset, limit, city);
      
      // Since RPC functions don't return count, we need to get it separately
      if (result.count === null) {
        const totalCount = await getTotalCount(filters, coordinates, city);
        return {
          ...result,
          count: totalCount
        };
      }
      
      return result;
    }

    // For distance range filtering with coordinates
    if (filters.distanceRange > 0 && coordinates) {
      console.log('🎯 Using distance range filtering with RPC');
      
      const result = await executeDistanceQuery(coordinates, filters, offset, limit, city);
      
      // Get total count for distance range filtering
      if (result.count === null) {
        const totalCount = await getTotalCount(filters, coordinates, city);
        return {
          ...result,
          count: totalCount
        };
      }
      
      return result;
    }

    // For intelligent sorting with coordinates but no distance filter, use hybrid approach
    if (coordinates && (!filters.sortOrder || filters.sortOrder === undefined)) {
      console.log('🎯 Using hybrid approach: intelligent sorting with proper pagination');
      
      // Get total count first for infinite scroll
      const totalCount = await getTotalCount(filters, coordinates, city);
      console.log(`📊 Total count: ${totalCount}`);
      
      // For first page or small datasets, fetch more data for better sorting
      if (offset === 0 || totalCount <= 100) {
        const fetchLimit = Math.min(Math.max(limit * 3, 60), totalCount || 60);
        
        let query = buildStandardQuery(filters, city);
        query = applySorting(query, filters.sortOrder);
        query = query.range(0, fetchLimit - 1);
        
        if (signal) {
          query = query.abortSignal(signal);
        }

        const { data: dealersData, error: dealersError } = await query;

        if (dealersError) {
          console.error("Error fetching dealers for intelligent sorting:", dealersError);
          throw dealersError;
        }

        if (!dealersData || dealersData.length === 0) {
          return { data: [], error: null, count: totalCount };
        }

        console.log(`📦 Fetched ${dealersData.length} dealers for intelligent sorting`);

        // Apply intelligent client-side sorting
        const sortedDealers = applyEnhancedSorting(dealersData, filters.sortOrder, coordinates);
        
        // Apply pagination to sorted results
        const paginatedDealers = sortedDealers.slice(offset, offset + limit);
        
        console.log(`✅ Returning ${paginatedDealers.length} dealers after intelligent sorting and pagination`);

        return {
          data: paginatedDealers,
          error: null,
          count: totalCount // Use real total count for infinite scroll
        };
      }
    }

    // Standard database pagination for other cases
    console.log('📍 Using standard database pagination');
    
    let query = buildStandardQuery(filters, city);
    query = applySorting(query, filters.sortOrder);
    query = query.range(offset, offset + limit - 1);
    
    if (signal) {
      query = query.abortSignal(signal);
    }

    const { data: dealersData, error: dealersError, count } = await query;

    if (dealersError) {
      console.error("Error fetching dealers with standard pagination:", dealersError);
      throw dealersError;
    }

    if (!dealersData || dealersData.length === 0) {
      return { data: [], error: null, count: count || 0 };
    }

    console.log(`📦 Fetched ${dealersData.length} dealers with standard pagination`);

    // Apply enhanced sorting if coordinates are available
    let finalDealers = dealersData;
    if (coordinates) {
      finalDealers = applyEnhancedSorting(dealersData, filters.sortOrder, coordinates);
    }
    
    console.log(`✅ Returning ${finalDealers.length} dealers with count ${count}`);

    return {
      data: finalDealers,
      error: null,
      count: count || finalDealers.length
    };

  } catch (error: any) {
    console.error("Error in executeEnhancedQuery:", error);
    throw error;
  }
};
